import axiosInstance from './axiosInstance';
import type { ISellerOnboardingStatus } from '../../types/auth.types';

export interface ProductInfo {
  productId: string;
  productName: string;
  totalSales: number;
  totalEarnings: number;
  pendingEarnings: number;
  totalRevenue: number;
  pendingRevenue: number;
  orderCount: number;
  confirmedOrderCount: number;
  averageUnitPrice: number;
  averageSellerProfit: number;
  profitMargin: number;
  salesLast30Days: number;
  salesLast7Days: number;
}

export interface ProductSalesData {
  designId: string;
  designTitle: string;
  designPreviewUrl?: string;
  // Aggregated across all products for this design
  totalSales: number; // Total quantity sold (confirmed orders) across all products
  totalEarnings: number; // Total seller profit from PAID orders only
  pendingEarnings: number; // Total seller profit from confirmed but not yet paid orders (mainly COD)
  totalRevenue: number; // Total revenue from PAID orders
  pendingRevenue: number; // Total revenue from confirmed but not yet paid orders
  orderCount: number; // Number of paid orders containing this design
  confirmedOrderCount: number; // Number of confirmed orders (including pending payment)
  lastSaleDate?: string;
  firstSaleDate?: string; // First sale date
  // Additional details for better display
  averageUnitPrice: number; // Average selling price per unit across all products
  averageSellerProfit: number; // Average seller profit per unit
  profitMargin: number; // Profit margin percentage
  averageOrderValue: number; // Average order value for this design
  // Sales trends
  salesLast30Days: number; // Sales in last 30 days
  salesLast7Days: number; // Sales in last 7 days
  // Products sold with this design
  products?: ProductInfo[]; // Will be populated when expanded
  productCount?: number; // Number of unique products sold with this design
}

export interface ProductOrderDetail {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string;
  itemFulfillmentStatus: string;
  itemPaymentStatus: string;
  sellerDisplayStatus: string;
  sellerPaymentStatus: string;
  orderDate: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  sellerProfit: number;
  size?: string;
  color?: string;
  colorHex?: string;
  selectedColor?: string;
}

export interface DashboardSummary {
  totalRevenue: number; // From paid orders only
  totalEarnings: number; // Seller profit from paid orders only
  pendingRevenue: number; // Revenue from confirmed but not yet paid orders
  pendingEarnings: number; // Seller profit from confirmed but not yet paid orders
  totalOrders: number; // Paid orders count
  confirmedOrders: number; // All confirmed orders (including pending payment)
  totalProductsSold: number; // Unique products/designs sold (from paid orders)
  totalUnitsSold: number; // Units sold (from paid orders)
  confirmedUnitsSold: number; // Units in confirmed orders (including pending)
  pendingOrders: number;
  averageOrderValue: number;
}

export interface SellerStoreTrafficSourceSlice {
  views: number;
  uniqueVisitors: number;
}

export interface SellerStoreTrafficSourceBreakdown {
  storefront: SellerStoreTrafficSourceSlice;
  listing: SellerStoreTrafficSourceSlice;
}

export interface SellerStoreFunnelMetrics {
  storePageViews: number;
  productPageViews: number;
  addToCartEvents: number;
  checkoutStartedEvents: number;
  paidOrders: number;
}

export interface SellerStoreMetricDelta {
  value: number;
  previousValue: number;
  changePercent: number | null;
}

export interface SellerStorefrontMetricDeltas {
  storePageViews: SellerStoreMetricDelta;
  productPageViews: SellerStoreMetricDelta;
  uniqueVisitors: SellerStoreMetricDelta;
  paidOrders: SellerStoreMetricDelta;
  totalRevenue: SellerStoreMetricDelta;
}

export interface SellerStoreFocusInsight {
  stage: 'pre_live' | 'traffic' | 'browse' | 'cart' | 'purchase' | 'scale';
  title: string;
  message: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface SellerStoreDailyTimeSeriesPoint {
  date: string;
  storePageViews: number;
  productPageViews: number;
  addToCartEvents: number;
  checkoutStartedEvents: number;
  paidOrders: number;
  revenue: number;
}

export interface SellerStorefrontTopProduct {
  designId: string;
  designTitle: string;
  designPreviewUrl?: string;
  catalogViews: number;
  unitsSold: number;
  revenue: number;
  salesLast7Days: number;
  conversionRatePercent: number | null;
}

export interface SellerStoreRecentVisitor {
  id: string;
  visitedAt: string;
  visitorKind: 'guest' | 'member';
  displayLabel: string;
  source: 'storefront' | 'listing';
  contextLabel: string | null;
}

export interface SellerStorefrontMetrics {
  period: '7d' | '30d' | 'all';
  productsPublished: number;
  draftListings: number;
  isLive: boolean;
  storePageViews: number;
  productPageViews: number;
  storeVisits: number;
  uniqueVisitors: number;
  uniqueVisitors30Days: number;
  paidOrders: number;
  confirmedOrders: number;
  unitsSold: number;
  salesLast7Days: number;
  salesLast30Days: number;
  totalRevenue: number;
  totalEarnings: number;
  averageOrderValue: number;
  conversionRatePercent: number | null;
  trafficSources: SellerStoreTrafficSourceBreakdown;
  funnel: SellerStoreFunnelMetrics;
  deltas: SellerStorefrontMetricDeltas | null;
  focusInsight: SellerStoreFocusInsight;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const sellerService = {
  /**
   * Get product sales and earnings for the current seller
   */
  async getProductSales(): Promise<ProductSalesData[]> {
    const response = await axiosInstance.get<ProductSalesData[]>('/seller/product-sales');
    return response.data;
  },

  /**
   * Get seller dashboard summary
   */
  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await axiosInstance.get<DashboardSummary>('/seller/dashboard-summary');
    return response.data;
  },

  async getStorefront(period: '7d' | '30d' | 'all' = '30d'): Promise<SellerStorefrontMetrics> {
    const response = await axiosInstance.get<SellerStorefrontMetrics>('/seller/storefront', {
      params: { period },
    });
    return response.data;
  },

  async getStorefrontTimeSeries(period: '7d' | '30d' | 'all' = '30d'): Promise<{
    period: '7d' | '30d' | 'all';
    points: SellerStoreDailyTimeSeriesPoint[];
  }> {
    const response = await axiosInstance.get<{
      period: '7d' | '30d' | 'all';
      points: SellerStoreDailyTimeSeriesPoint[];
    }>('/seller/storefront/timeseries', { params: { period } });
    return response.data;
  },

  async getStorefrontTopProducts(
    page = 1,
    limit = 5,
    period: '7d' | '30d' | 'all' = '30d',
  ): Promise<PaginatedResponse<SellerStorefrontTopProduct>> {
    const response = await axiosInstance.get<PaginatedResponse<SellerStorefrontTopProduct>>(
      '/seller/storefront/top-products',
      { params: { page, limit, period } },
    );
    return response.data;
  },

  async getStorefrontRecentVisitors(
    page = 1,
    limit = 5,
    source: 'all' | 'storefront' | 'listing' = 'all',
    period: '7d' | '30d' | 'all' = '30d',
  ): Promise<PaginatedResponse<SellerStoreRecentVisitor>> {
    const response = await axiosInstance.get<PaginatedResponse<SellerStoreRecentVisitor>>(
      '/seller/storefront/recent-visitors',
      { params: { page, limit, source, period } },
    );
    return response.data;
  },

  /**
   * Get products sold with a specific design
   */
  async getDesignProducts(designId: string): Promise<ProductInfo[]> {
    const response = await axiosInstance.get<ProductInfo[]>(`/seller/product-sales/${designId}/products`);
    return response.data;
  },

  /**
   * Get all orders for a design (across all products) with pagination
   */
  async getDesignOrders(designId: string, limit: number = 50, offset: number = 0): Promise<{ orders: Array<ProductOrderDetail & { productId: string; productName: string }>; total: number }> {
    const response = await axiosInstance.get<{ orders: Array<ProductOrderDetail & { productId: string; productName: string }>; total: number }>(
      `/seller/product-sales/${designId}/orders?limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  /**
   * Get individual orders for a specific design/product with pagination
   */
  async getProductOrders(designId: string, productId: string, limit: number = 50, offset: number = 0): Promise<{ orders: ProductOrderDetail[]; total: number }> {
    const response = await axiosInstance.get<{ orders: ProductOrderDetail[]; total: number }>(
      `/seller/product-sales/${designId}/${productId}/orders?limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  /**
   * Get comprehensive order details for seller (Etsy-level)
   */
  async getOrderDetails(orderId: string): Promise<OrderDetailsForSeller> {
    const response = await axiosInstance.get<OrderDetailsForSeller>(`/seller/orders/${orderId}`);
    return response.data;
  },

  /**
   * Get all orders for seller with pagination
   */
  async getAllOrders(
    opts: {
      limit?: number;
      offset?: number;
      search?: string;
      itemFulfillmentStatus?: string;
      itemPaymentStatus?: string;
      orderStatus?: string;
      paymentStatus?: string;
      from?: string;
      to?: string;
    } = {},
  ): Promise<{ orders: SellerOrderListItem[]; total: number }> {
    const search = new URLSearchParams();
    if (opts.limit != null) search.set('limit', String(opts.limit));
    if (opts.offset != null) search.set('offset', String(opts.offset));
    Object.entries(opts).forEach(([k, v]) => {
      if (k === 'limit' || k === 'offset') return;
      if (v !== undefined && v !== '') search.set(k, v);
    });
    const q = search.toString();
    const response = await axiosInstance.get<{ orders: SellerOrderListItem[]; total: number }>(
      `/seller/orders${q ? `?${q}` : ''}`,
    );
    return response.data;
  },

  async getSellerOrderActivity(opts: {
    limit?: number;
    offset?: number;
  } = {}): Promise<{ items: SellerOrderActivityItem[]; total: number }> {
    const search = new URLSearchParams();
    if (opts.limit != null) search.set('limit', String(opts.limit));
    if (opts.offset != null) search.set('offset', String(opts.offset));
    const q = search.toString();
    const response = await axiosInstance.get<{ items: SellerOrderActivityItem[]; total: number }>(
      `/seller/order-activity${q ? `?${q}` : ''}`,
    );
    return response.data;
  },

  async getFinanceOverview(): Promise<SellerFinanceOverview> {
    const response = await axiosInstance.get<SellerFinanceOverview>('/seller/finance/overview');
    return response.data;
  },

  async getFinanceEarnings(params: Record<string, string | undefined>): Promise<{ rows: SellerEarningRow[]; total: number }> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') search.set(k, v);
    });
    const q = search.toString();
    const response = await axiosInstance.get<{ rows: SellerEarningRow[]; total: number }>(
      `/seller/finance/earnings${q ? `?${q}` : ''}`
    );
    return response.data;
  },

  async getFinanceLedger(params: Record<string, string | undefined>): Promise<{ rows: SellerLedgerRow[]; total: number }> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') search.set(k, v);
    });
    const q = search.toString();
    const response = await axiosInstance.get<{ rows: SellerLedgerRow[]; total: number }>(
      `/seller/finance/ledger${q ? `?${q}` : ''}`
    );
    return response.data;
  },

  async getFinancePayouts(): Promise<SellerPayoutRequest[]> {
    const response = await axiosInstance.get<SellerPayoutRequest[]>('/seller/finance/payouts');
    return response.data;
  },

  async getFinanceAdjustments(): Promise<SellerLedgerRow[]> {
    const response = await axiosInstance.get<SellerLedgerRow[]>('/seller/finance/adjustments');
    return response.data;
  },

  async getFinanceWalletSnapshot(): Promise<SellerWalletSnapshot> {
    const response = await axiosInstance.get<SellerWalletSnapshot>('/seller/finance/wallet-snapshot');
    return response.data;
  },

  async getFinanceWalletActivity(limit = 80): Promise<{ items: SellerWalletActivityItem[]; total: number }> {
    const response = await axiosInstance.get<{ items: SellerWalletActivityItem[]; total: number }>(
      `/seller/finance/wallet-activity?limit=${limit}`,
    );
    return response.data;
  },

  async requestFinancePayout(body: {
    amount: number;
    payoutMethodId?: string;
    payoutMethodNote?: string;
  }): Promise<SellerPayoutRequest> {
    const response = await axiosInstance.post<SellerPayoutRequest>('/seller/finance/payout-requests', body);
    return response.data;
  },

  async listPayoutMethods(): Promise<SellerPayoutMethod[]> {
    const response = await axiosInstance.get<SellerPayoutMethod[]>('/seller/payout-methods');
    return response.data;
  },

  async createPayoutMethod(body: CreateSellerPayoutMethodInput): Promise<SellerPayoutMethod> {
    const response = await axiosInstance.post<SellerPayoutMethod>('/seller/payout-methods', body);
    return response.data;
  },

  async updatePayoutMethod(id: string, body: UpdateSellerPayoutMethodInput): Promise<SellerPayoutMethod> {
    const response = await axiosInstance.patch<SellerPayoutMethod>(`/seller/payout-methods/${id}`, body);
    return response.data;
  },

  async setDefaultPayoutMethod(id: string): Promise<SellerPayoutMethod> {
    const response = await axiosInstance.patch<SellerPayoutMethod>(`/seller/payout-methods/${id}/default`);
    return response.data;
  },

  async deactivatePayoutMethod(id: string): Promise<void> {
    await axiosInstance.delete(`/seller/payout-methods/${id}`);
  },

  async getSellerOnboardingStatus(): Promise<ISellerOnboardingStatus> {
    const response = await axiosInstance.get<ISellerOnboardingStatus>('/seller/onboarding/status');
    return response.data;
  },

  async completeSellerOnboarding(acceptTerms = true): Promise<{ message: string }> {
    const response = await axiosInstance.post<{ message: string }>('/seller/onboarding/complete', {
      acceptTerms,
    });
    return response.data;
  },
};

export interface SellerWalletBreakdown {
  availableGross: number;
  availableWithdrawable: number;
  payoutReserve: number;
  outstandingDebt: number;
}

export interface SellerReleaseSettlementSummary {
  orderId: string;
  orderItemId: string;
  orderNumber: string | null;
  releaseGross: number;
  debtRecovered: number;
  creditedToAvailable: number;
  availableGrossBeforeRelease: number;
  createdAt: string;
}

export interface SellerWalletSnapshotLine {
  label: string;
  amount: number;
  tone: 'neutral' | 'positive' | 'negative' | 'emphasis';
  hint: string | null;
}

export interface SellerWalletSnapshot {
  asOf: string;
  pendingBalance: number;
  availableGross: number;
  payoutReserve: number;
  outstandingDebt: number;
  withdrawable: number;
  lifetimeEarnings: number;
  totalPaidOut: number;
  postReleaseClawbacksTotal: number;
  lines: SellerWalletSnapshotLine[];
  openPayouts: Array<{ id: string; amount: number; status: string; createdAt: string }>;
}

export interface SellerWalletActivityItem {
  id: string;
  kind: 'ledger' | 'payout';
  at: string;
  title: string;
  detail: string | null;
  orderNumber: string | null;
  transactionType: string | null;
  payoutRequestId: string | null;
  payoutStatus: string | null;
  pendingDelta: number;
  availableDelta: number;
  lifetimeDelta: number;
  amount: number | null;
  pendingBalanceAfter: number;
  availableGrossAfter: number;
  withdrawableAfter: number;
  payoutReserveAfter: number;
  outstandingDebtAfter: number;
}

export interface SellerFinanceOverview {
  pendingBalance: number;
  availableBalance: number;
  availableGross?: number;
  payoutReserve?: number;
  outstandingDebt: number;
  lifetimeEarnings: number;
  totalPaidOut: number;
  negativeAdjustmentsTotal: number;
  postReleaseClawbacksTotal: number;
  /** Net SUM(lifetimeEarningDelta) since calendar month start (all ledger types). */
  currentMonthEarnings: number;
  nextAvailablePayoutAmount: number | null;
  nextAvailablePayoutDate: string | null;
  minPayoutAmount: number;
  protectionDays: number;
  walletBreakdown?: SellerWalletBreakdown;
  lastReleaseSettlement?: SellerReleaseSettlementSummary | null;
}

export interface SellerEarningRow {
  orderItemId: string;
  orderId: string;
  orderNumber: string;
  orderWorkflowStatus: string;
  aggregateFulfillmentStatus: string | null;
  orderPaymentStatus: string;
  itemFulfillmentStatus: string;
  itemPaymentStatus: string;
  designTitle: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  sellerProfitTotal: number;
  walletPendingAmount?: number | null;
  walletReleaseGross?: number | null;
  walletCreditedToAvailable?: number | null;
  walletDebtSettledOnRelease?: number | null;
  balanceStatus: string;
  availableAt: string | null;
  createdAt: string;
}

export interface SellerLedgerRow {
  id: string;
  transactionType: string;
  amount: number;
  pendingDelta: number;
  availableDelta: number;
  lifetimeEarningDelta: number;
  paidOutDelta: number;
  orderId: string | null;
  orderItemId: string | null;
  availableAt: string | null;
  reason: string | null;
  createdAt: string;
}

export interface SellerPayoutRequest {
  id: string;
  sellerId: string;
  amount: string;
  status: string;
  payoutMethodNote: string | null;
  payoutMethodId: string | null;
  payoutDestinationSnapshot: PayoutDestinationSnapshot | null;
  rejectionReason: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  paidByUserId: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SellerPayoutMethodType = 'BANK' | 'PAYPAL' | 'STRIPE' | 'WISE' | 'PAYONEER';

export interface SellerPayoutMethod {
  id: string;
  type: SellerPayoutMethodType;
  label: string;
  accountHolderName: string | null;
  bankName: string | null;
  paypalEmail: string | null;
  stripeAccountId: string | null;
  stripeStatus: string | null;
  maskedIdentifier: string;
  displayName: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSellerPayoutMethodInput {
  type: SellerPayoutMethodType;
  label: string;
  accountHolderName?: string;
  bankName?: string;
  ribOrIban?: string;
  paypalEmail?: string;
  stripeAccountId?: string;
  stripeStatus?: string;
  isDefault?: boolean;
}

export interface UpdateSellerPayoutMethodInput {
  label?: string;
  accountHolderName?: string;
  bankName?: string;
  ribOrIban?: string;
  paypalEmail?: string;
  stripeAccountId?: string;
  stripeStatus?: string;
  isActive?: boolean;
}

export interface PayoutDestinationSnapshot {
  type: SellerPayoutMethodType;
  label: string;
  bankName?: string | null;
  accountHolderName?: string | null;
  maskedIdentifier?: string | null;
  paypalEmail?: string | null;
  stripeAccountId?: string | null;
  stripeStatus?: string | null;
  displayName: string;
  ribOrIban?: string | null;
}

export interface SellerOrderActivityItem {
  id: string;
  orderId: string;
  orderNumber: string;
  eventType: string;
  title: string;
  detail: string | null;
  at: string;
  scope: 'order' | 'line';
  orderItemId: string | null;
  designTitle: string | null;
  actorLabel: 'platform' | 'system';
}

export interface SellerOrderListItem {
  orderId: string;
  orderNumber: string;
  sellerDisplayStatus: string;
  sellerPaymentStatus: string;
  orderStatus: string;
  paymentStatus: string;
  orderDate: string;
  customerName: string;
  customerEmail: string;
  /** Seller's lines subtotal only (not full order total). */
  totalAmount: number;
  sellerProfit: number;
  itemCount: number;
  designCount: number;
}

export interface OrderDetailsForSeller {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string;
  sellerDisplayStatus: string;
  sellerPaymentStatus: string;
  orderDate: string;
  customer: {
    name: string;
    email: string;
    phone?: string;
  };
  shippingAddress: {
    name: string;
    address: string;
    city: string;
    zip: string;
    country: string;
    phone?: string;
  };
  items: Array<{
    id: string;
    designId: string;
    designTitle: string;
    designPreviewUrl?: string;
    productId: string;
    productName: string;
    variantId: string;
    size: string;
    color: string;
    colorHex?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    sellerProfit: number;
    sku: string;
    itemFulfillmentStatus: string;
    itemPaymentStatus: string;
  }>;
  totals: {
    subtotal: number;
    shipping: number;
    tax: number;
    discount: number;
    total: number;
    totalSellerProfit: number;
  };
  fulfillment?: {
    trackingNumber?: string;
    carrier?: string;
    estimatedDelivery?: string;
    shippedAt?: string;
    deliveredAt?: string;
  };
  timeline?: Array<{
    eventType: string;
    description: string;
    createdAt: string;
  }>;
}
